// Catalog Admin — listing page (2026-05-12 #9).
//
// Admin-only browse of the canonical 6,721 merchd_products. Filters:
// supplier (PCNA / SanMar / All), category, has-image / no-image, has-
// decorated-price / no-decorated-price, live / hidden, free-text name
// search. Cursor pagination via /admin/catalog/products.
//
// Each row links to /admin/catalog/products/:id for full detail + edit.
// Hidden rows render with a "Hidden" pill + dimmed thumbnail.
//
// Pattern mirrors CategoriesAdminPage / ClientsListPage: TanStack Query,
// brand-locked palette, no Hook Form, Escape to clear search.

import { useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface AdminProductListRow {
  id: string;
  name: string;
  brand: string | null;
  sku: string | null;
  category: { slug: string; name: string } | null;
  primaryImageUrl: string | null;
  lowestBlankPiecePrice: string | null;
  lowestDecoratedPiecePrice: string | null;
  isActive: boolean;
  supplierSlugs: string[];
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  rows: AdminProductListRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

interface SupplierFacet {
  slug: string;
  name: string;
  productCount: number;
}

interface SuppliersResponse {
  suppliers: SupplierFacet[];
}

type ImageFilter = "any" | "has_image" | "no_image";
type PriceFilter = "any" | "has_decorated" | "no_decorated";
type VisibilityFilter = "any" | "live" | "hidden";

const IMAGE_LABELS: Record<ImageFilter, string> = {
  any: "All images",
  has_image: "Has image",
  no_image: "Missing image",
};

const PRICE_LABELS: Record<PriceFilter, string> = {
  any: "All prices",
  has_decorated: "Has decorated price",
  no_decorated: "Inquire only",
};

const VIS_LABELS: Record<VisibilityFilter, string> = {
  any: "Live + hidden",
  live: "Live only",
  hidden: "Hidden only",
};

export function CatalogProductsListPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;

  // Filters live in the address, so "Back to results" on a product returns to
  // the same filtered list (Stephanie 2026-11-02).
  const [params, setParams] = useSearchParams();
  const [supplier, setSupplier] = useState<string>(() => params.get("supplier") ?? "");
  const [image, setImage] = useState<ImageFilter>(() => (params.get("image") as ImageFilter | null) ?? "any");
  const [price, setPrice] = useState<PriceFilter>(() => (params.get("price") as PriceFilter | null) ?? "any");
  const [visibility, setVisibility] = useState<VisibilityFilter>(() => (params.get("visibility") as VisibilityFilter | null) ?? "any");
  const [search, setSearch] = useState(() => params.get("q") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(() => params.get("q") ?? "");

  // Debounce search input so we don't fire a request per keystroke.
  useMemoDebounce(search, 300, setDebouncedSearch);

  useEffect(() => {
    const next = new URLSearchParams();
    if (supplier) next.set("supplier", supplier);
    if (image !== "any") next.set("image", image);
    if (price !== "any") next.set("price", price);
    if (visibility !== "any") next.set("visibility", visibility);
    if (debouncedSearch.trim()) next.set("q", debouncedSearch.trim());
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [supplier, image, price, visibility, debouncedSearch, params, setParams]);

  const queryKey = useMemo(
    () =>
      [
        "admin",
        "catalog",
        "products",
        {
          supplier,
          image,
          price,
          visibility,
          search: debouncedSearch,
        },
      ] as const,
    [supplier, image, price, visibility, debouncedSearch],
  );

  const listQ = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (supplier) params.set("supplier", supplier);
      if (image !== "any") params.set("image", image);
      if (price !== "any") params.set("price", price);
      if (visibility !== "any") params.set("visibility", visibility);
      if (debouncedSearch.trim().length > 0) {
        params.set("search", debouncedSearch.trim());
      }
      params.set("limit", "60");
      const qs = params.toString();
      return api<ListResponse>(
        `/admin/catalog/products${qs ? `?${qs}` : ""}`,
      );
    },
    enabled: isAdmin,
  });

  const suppliersQ = useQuery({
    queryKey: ["admin", "catalog", "suppliers"] as const,
    queryFn: () => api<SuppliersResponse>("/admin/catalog/suppliers"),
    enabled: isAdmin,
    staleTime: 60_000,
  });

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full">
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Catalog
        </h1>
        <p className="mt-3 text-sm text-brown-300">
          This admin surface is only visible to users with the admin role.
        </p>
      </div>
    );
  }

  const rows = listQ.data?.rows ?? [];
  const suppliers = suppliersQ.data?.suppliers ?? [];

  const clearFilters = (): void => {
    setSupplier("");
    setImage("any");
    setPrice("any");
    setVisibility("any");
    setSearch("");
    setDebouncedSearch("");
  };

  const filtersActive =
    supplier !== "" ||
    image !== "any" ||
    price !== "any" ||
    visibility !== "any" ||
    debouncedSearch.trim().length > 0;

  return (
    <div className="mx-auto w-full space-y-8">
      {/* Header */}
      <div>
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Admin · Catalog
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Products
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-brown-400">
          Every canonical Other product across all suppliers. Toggle
          visibility, edit metadata, swap images, reassign category. Hidden
          products stay in the database but do not render on the public
          storefront.
        </p>
      </div>

      {/* Filter bar */}
      <div className="space-y-3 border-y border-brown-50 py-4">
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="Search">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setSearch("");
                  setDebouncedSearch("");
                }
              }}
              placeholder="Filter by name…"
              className="w-64"
            />
          </FilterField>

          <FilterField label="Supplier">
            <SupplierPicker value={supplier} onChange={(v) => setSupplier(v)} suppliers={suppliers} />
          </FilterField>

          <FilterField label="Image">
            <Select
              value={image}
              onChange={(v) => setImage(v as ImageFilter)}
              options={(["any", "has_image", "no_image"] as ImageFilter[]).map(
                (v) => ({ value: v, label: IMAGE_LABELS[v] }),
              )}
            />
          </FilterField>

          <FilterField label="Price">
            <Select
              value={price}
              onChange={(v) => setPrice(v as PriceFilter)}
              options={(
                ["any", "has_decorated", "no_decorated"] as PriceFilter[]
              ).map((v) => ({ value: v, label: PRICE_LABELS[v] }))}
            />
          </FilterField>

          <FilterField label="Visibility">
            <Select
              value={visibility}
              onChange={(v) => setVisibility(v as VisibilityFilter)}
              options={(
                ["any", "live", "hidden"] as VisibilityFilter[]
              ).map((v) => ({ value: v, label: VIS_LABELS[v] }))}
            />
          </FilterField>

          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-baseline gap-3 text-xs text-brown-300">
          <span>
            {listQ.isLoading
              ? "Loading…"
              : `${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"}`}
            {listQ.data?.nextCursor && " · more available — refine to narrow"}
          </span>
        </div>
      </div>

      {/* Body */}
      {listQ.isError && (
        <p className="text-sm text-rust-600">Could not load the catalog.</p>
      )}
      {!listQ.isLoading && !listQ.isError && rows.length === 0 && (
        <div className="rounded-lg border border-brown-50 bg-white px-5 py-12 text-center">
          <p className="text-sm text-brown-300">
            No products match the current filter.
            {filtersActive && (
              <>
                {" "}
                <button
                  type="button"
                  className="underline hover:text-brown-500"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
                .
              </>
            )}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {rows.map((row) => (
            <ProductCard key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Card per product ─────────────────────────────────────────────────────

function ProductCard({ row }: { row: AdminProductListRow }) {
  const decorated = row.lowestDecoratedPiecePrice;
  const blank = row.lowestBlankPiecePrice;
  const location = useLocation();
  return (
    <Link
      to={`/admin/catalog/products/${row.id}`}
      state={{ backTo: `${location.pathname}${location.search}` }}
      className="group flex flex-col overflow-hidden rounded-lg border border-brown-100 bg-white transition-colors hover:border-peach-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50"
    >
      <div
        className={cn(
          "relative aspect-square bg-cream-100",
          !row.isActive && "opacity-50",
        )}
      >
        {row.primaryImageUrl ? (
          <img
            src={row.primaryImageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[11px] uppercase tracking-wide text-brown-300">
            No image
          </div>
        )}
        {!row.isActive && (
          <div className="absolute left-2 top-2">
            <StatusPill tone="warn">Hidden</StatusPill>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
          {row.supplierSlugs.length > 0
            ? row.supplierSlugs.join(" · ")
            : "—"}
        </p>
        <h3 className="line-clamp-2 text-sm font-semibold text-brown-500 group-hover:text-brown-600">
          {row.name}
        </h3>
        {(row.sku || row.brand) && (
          <p className="text-xs text-brown-300">{[row.sku, row.brand].filter(Boolean).join(" · ")}</p>
        )}
        <div className="mt-1 text-xs">
          {row.category ? (
            <span className="text-brown-400">{row.category.name}</span>
          ) : (
            <span className="text-rust-500">Uncategorized</span>
          )}
        </div>
        <div className="mt-auto pt-2 text-xs">
          {decorated ? (
            <span className="font-semibold text-brown-500">
              Decorated ${Number(decorated).toFixed(2)}
            </span>
          ) : blank ? (
            <span className="text-brown-400">
              Blank ${Number(blank).toFixed(2)} · inquire decorated
            </span>
          ) : (
            <span className="text-rust-500">Inquire for pricing</span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Small components ─────────────────────────────────────────────────────

function FilterField(props: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-brown-300">
        {props.label}
      </span>
      {props.children}
    </label>
  );
}

function Select(props: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      className="h-10 rounded-md border border-brown-100 bg-white px-3 text-sm text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
    >
      {props.options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// Type a few letters to find a supplier (Stephanie 2026-11-02: "you should be
// able to type a few letters of the vendor"). A–Z; empty = all suppliers.
function SupplierPicker(props: {
  value: string;
  onChange: (v: string) => void;
  suppliers: Array<{ slug: string; name: string; productCount: number }>;
}) {
  const chosen = props.suppliers.find((s) => s.slug === props.value) ?? null;
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const q = text.trim().toLowerCase();
  const matches = q ? props.suppliers.filter((s) => s.name.toLowerCase().includes(q)) : props.suppliers;
  const pick = (v: string) => {
    props.onChange(v);
    setText("");
    setOpen(false);
  };
  return (
    <div className="relative w-56">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label="Supplier"
        value={open ? text : chosen ? chosen.name : ""}
        placeholder={chosen ? chosen.name : "All suppliers — type to find"}
        onFocus={() => { setText(""); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && matches[0]) { e.preventDefault(); pick(matches[0].slug); }
          if (e.key === "Escape") setOpen(false);
        }}
        className="h-10 w-full rounded-md border border-brown-100 bg-white px-3 pr-8 text-sm text-brown-500 placeholder:text-brown-300 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200"
      />
      {chosen && !open && (
        <button type="button" aria-label="Show all suppliers" onClick={() => pick("")} className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-sm text-brown-300 hover:text-brown-500">
          ✕
        </button>
      )}
      {open && (
        <ul role="listbox" className="absolute z-20 mt-1 max-h-72 w-72 overflow-auto rounded-md border border-brown-100 bg-white py-1 text-sm shadow-lg">
          {!q && (
            <li role="option" aria-selected={!props.value} onMouseDown={(e) => { e.preventDefault(); pick(""); }} className="cursor-pointer px-3 py-1.5 text-brown-500 hover:bg-cream-100">
              All suppliers
            </li>
          )}
          {matches.map((s) => (
            <li key={s.slug} role="option" aria-selected={s.slug === props.value} onMouseDown={(e) => { e.preventDefault(); pick(s.slug); }} className={`cursor-pointer px-3 py-1.5 hover:bg-cream-100 ${s.slug === props.value ? "font-semibold text-brown-500" : "text-brown-500"}`}>
              {s.name} <span className="text-brown-300">({s.productCount.toLocaleString()})</span>
            </li>
          ))}
          {matches.length === 0 && <li className="px-3 py-1.5 text-brown-300">No supplier matches “{text.trim()}”</li>}
        </ul>
      )}
    </div>
  );
}

// Debounce a value into a setter without pulling in lodash.
import { useEffect } from "react";
function useMemoDebounce<T>(
  value: T,
  delay: number,
  setter: (v: T) => void,
): void {
  useEffect(() => {
    const handle = setTimeout(() => setter(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay, setter]);
}
